import axios from 'axios';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '../utils/safeStorage';
import { getTenantId } from '../utils/tenantHelper';
import { API_BASE_URL } from './apiRegistry';

// Debug logging
console.log('Regular API Base URL:', API_BASE_URL);
console.log('Environment REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant header
api.interceptors.request.use(
  (config) => {
    const token = getLocalStorageItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant header for multi-tenancy support
    const tenantId = getTenantId();
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    // Add detailed logging for member update requests
    if (config.method === 'put' && config.url?.includes('/members/')) {
      console.log('📡 [API INTERCEPTOR] Member update request intercepted');
      console.log('📡 [API INTERCEPTOR] Full URL:', config.baseURL + config.url);
      console.log('📡 [API INTERCEPTOR] Method:', config.method);
      console.log('📡 [API INTERCEPTOR] Request data:', JSON.stringify(config.data, null, 2));
      console.log('📡 [API INTERCEPTOR] Request headers:', {
        'Content-Type': config.headers['Content-Type'],
        'Authorization': config.headers.Authorization ? 'Bearer [TOKEN]' : 'None'
      });
      console.log('📡 [API INTERCEPTOR] Request config:', {
        timeout: config.timeout,
        withCredentials: config.withCredentials
      });
    }
    
    return config;
  },
  (error) => {
    console.error('❌ [API INTERCEPTOR] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Add detailed logging for member update responses
    if (response.config?.method === 'put' && response.config?.url?.includes('/members/')) {
      console.log('✅ [API INTERCEPTOR] Member update response received');
      console.log('✅ [API INTERCEPTOR] Status:', response.status, response.statusText);
      console.log('✅ [API INTERCEPTOR] Response data:', JSON.stringify(response.data, null, 2));
      console.log('✅ [API INTERCEPTOR] Response headers:', response.headers);
    }
    return response.data;
  },
  (error) => {
    // Add detailed logging for member update errors
    if (error.config?.method === 'put' && error.config?.url?.includes('/members/')) {
      console.error('❌ [API INTERCEPTOR] Member update error intercepted');
      console.error('❌ [API INTERCEPTOR] Error status:', error.response?.status);
      console.error('❌ [API INTERCEPTOR] Error status text:', error.response?.statusText);
      console.error('❌ [API INTERCEPTOR] Error response data:', JSON.stringify(error.response?.data, null, 2));
      console.error('❌ [API INTERCEPTOR] Error response headers:', error.response?.headers);
      console.error('❌ [API INTERCEPTOR] Full error object:', error);
      console.error('❌ [API INTERCEPTOR] Request that failed:', {
        url: error.config?.url,
        method: error.config?.method,
        data: JSON.stringify(error.config?.data, null, 2)
      });
    }
    
    if (error.response?.status === 401) {
      const url = String(error.config?.url || '');
      const isAuthFlowRequest =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/register-player') ||
        url.includes('/auth/player-signup-config') ||
        url.includes('/auth/player-help-feature') ||
        url.includes('/auth/verify');
      // Never wipe token or hard-redirect on login/register/verify — breaks sign-in and looks like "can't log in"
      if (
        !isAuthFlowRequest &&
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/admin/')
      ) {
        console.log('🔐 Session invalid or expired, redirecting to team login');
        removeLocalStorageItem('token');
        window.location.href = '/login';
      }
    }
    // Return the full error object so we can access response.data in catch blocks
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getTenantsForPlayerSignup: () => api.get('/auth/tenants-for-player-signup'),
  /** Public — Swiss canton dropdown for player signup / profile (matches server canton mapping). */
  getPlayerSignupCantonOptions: () => api.get('/auth/player-signup-canton-options'),
  getPlayerSignupConfig: () => api.get('/auth/player-signup-config'),
  /** Public — whether the in-app player help assistant should be shown (Super Admin + DB flag). */
  getPlayerHelpFeature: () => api.get('/auth/player-help-feature'),
  /** Public — whether the in-app player help wizard should be shown (Super Admin + DB flag). */
  getPlayerHelpWizardFeature: () => api.get('/auth/player-help-wizard-feature'),
  /** Step 1: validate + send email code */
  registerPlayerStart: (data) => api.post('/auth/register-player/start', data),
  registerPlayerComplete: (data) => api.post('/auth/register-player/complete', data),
  registerPlayerResendCode: (data) => api.post('/auth/register-player/resend-code', data),
  verifyToken: () => api.get('/auth/verify'),
  getProfile: () => api.get('/auth/profile'),
  getSeasonStatus: (tenantId, seasonId) => {
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    if (seasonId) params.season_id = seasonId;
    return api.get('/auth/season-status', { params });
  },
};

// Teams API
export const teamsAPI = {
  getTeam: () => api.get('/teams/profile'),
  updateTeam: (data) => api.put('/teams/profile', data),
  uploadLogo: (file, teamId) => {
    const formData = new FormData();
    if (teamId) formData.append('team_id', teamId);
    formData.append('teamLogo', file);
    return api.post('/teams/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Members API
export const membersAPI = {
  getMembers: (teamId, seasonId, options = {}) => {
    const params = {};
    if (teamId) params.team_id = teamId;
    if (seasonId) params.season_id = seasonId;
    return api.get('/members', { params, ...options });
  },
  addMember: (data, seasonId) => api.post('/members', data, { params: seasonId ? { season_id: seasonId } : {} }),
  updateMember: (memberId, data, seasonId, teamId) => {
    const params = {};
    if (seasonId) params.season_id = seasonId;
    if (teamId) params.team_id = teamId;
    return api.put(`/members/${memberId}`, data, {
      params: Object.keys(params).length ? params : undefined,
    });
  },
  removeMember: (memberId, body, teamId) => {
    const params = {};
    if (teamId) params.team_id = teamId;
    return api.post(`/members/${memberId}/remove`, body, {
      params: Object.keys(params).length ? params : undefined,
    });
  },
  requestSelfRemoval: (body, teamId) => {
    const params = {};
    if (teamId) params.team_id = teamId;
    return api.post('/members/request-self-removal', body, {
      params: Object.keys(params).length ? params : undefined,
    });
  },
  uploadProfilePicture: (memberId, file, teamId) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    const params = teamId ? { team_id: teamId } : {};
    return api.post(`/members/${memberId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: Object.keys(params).length ? params : undefined,
    });
  },
};

// Payments API
export const paymentsAPI = {
  createPaymentIntent: (data) => api.post('/payments/create-intent', data),
  confirmPayment: (data) => api.post('/payments/confirm', data),
  getPaymentStatus: () => api.get('/payments/status'),
};

// Player API (Phase 2 – requires player JWT)
export const playerAPI = {
  getTenants: () => api.get('/player/tenants'),
  getSeasons: (tenantId) => api.get('/player/seasons', { params: { tenant_id: tenantId } }),
  getCampaigns: (tenantId) => api.get('/player/campaigns', { params: { tenant_id: tenantId } }),
  getCampaignTeamPresence: (campaignId, tenantId, seasonId) => {
    const params = { tenant_id: tenantId };
    if (seasonId) params.season_id = seasonId;
    return api.get(`/player/campaigns/${campaignId}/team-presence`, { params });
  },
  getCampaignDefaultSeason: (campaignId, tenantId) =>
    api.get(`/player/campaigns/${campaignId}/default-season`, { params: { tenant_id: tenantId } }),
  registerCampaignTeam: (campaignId, teamId, tenantId, body = {}) =>
    api.post(
      `/player/campaigns/${campaignId}/register-team`,
      { team_id: teamId, ...body },
      { params: tenantId ? { tenant_id: tenantId } : {} }
    ),
  checkTeamName: (body) => api.post('/player/registration/check-team-name', body),
  getEventTournaments: (tenantId) =>
    api.get('/player/event-tournaments', { params: tenantId ? { tenant_id: tenantId } : {} }),
  registerEventTournament: (eventId) => api.post(`/player/event-tournaments/${eventId}/register`),
  getTournaments: () => api.get('/player/tournaments'),
  getDashboardTournaments: () => api.get('/player/dashboard-tournaments'),
  getTeams: () => api.get('/player/teams'),
  getProfile: () => api.get('/player/profile'),
  getStats: () => api.get('/player/stats'),
  getRank: () => api.get('/player/rank'),
  /** Player help module (alpha); server skips route when HELP_ASSISTANT_ENABLED=false */
  getHelpAssistantStatus: () => api.get('/player-help/status'),
  getHelpTeamSeasons: (teamId) => api.get('/player-help/team-seasons', { params: { team_id: teamId } }),
  getHelpRankingContext: (teamId, seasonId) => {
    const params = { team_id: teamId };
    if (seasonId) params.season_id = seasonId;
    return api.get('/player-help/ranking-context', { params });
  },
  updateProfile: (data) => api.put('/player/profile', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    return api.post('/player/profile-picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeProfilePicture: () => api.delete('/player/profile-picture'),
  changePassword: (currentPassword, newPassword) => api.put('/player/change-password', { currentPassword, newPassword }),
  // Individual match system (friendly / ranked)
  getCantons: () => api.get('/player/cantons'),
  matchmakingEnter: (type, cantonCodes) => api.post('/player/matchmaking/enter', { type: type || 'friendly', cantonCodes: cantonCodes || [] }),
  matchmakingLeave: () => api.post('/player/matchmaking/leave'),
  matchmakingStatus: () => api.get('/player/matchmaking/status'),
  getMatchProposals: () => api.get('/player/match-proposals'),
  acceptMatchProposal: (proposalId) => api.post(`/player/match-proposals/${proposalId}/accept`),
  declineMatchProposal: (proposalId) => api.post(`/player/match-proposals/${proposalId}/decline`),
  getMatches: (status) => api.get('/player/matches', status ? { params: { status } } : {}),
  getMatch: (matchId) => api.get(`/player/matches/${matchId}`),
  updateMatch: (matchId, data) => api.patch(`/player/matches/${matchId}`, data),
  submitMatchScore: (matchId, score) => api.post(`/player/matches/${matchId}/score`, score),
  verifyMatchScore: (matchId, action, disputeMessage) =>
    api.post(`/player/matches/${matchId}/verify-score`, { action, disputeMessage }),
  cancelMatch: (matchId) => api.post(`/player/matches/${matchId}/cancel`),
  getConversations: () => api.get('/player/conversations'),
  getConversationMessages: (conversationId) => api.get(`/player/conversations/${conversationId}/messages`),
  sendMessage: (conversationId, body) => api.post(`/player/conversations/${conversationId}/messages`, { body }),
  postConversationViewing: (conversationId) => api.post(`/player/conversations/${conversationId}/viewing`),
  hideConversation: (conversationId) => api.post(`/player/conversations/${conversationId}/hide`),
  getOrganisedEvents: (params = {}) =>
    api.get('/player/organised-events', {
      params: {
        ...(params.eventType ? { event_type: params.eventType } : {}),
      },
    }),
  getOrganisedEvent: (id) => api.get(`/player/organised-events/${id}`),
  getOrganisedEventBookings: () => api.get('/player/organised-events/my-bookings'),
  checkoutOrganisedEvent: (id, payload = {}) =>
    api.post(`/player/organised-events/${id}/checkout`, payload),
  verifyOrganisedEventCheckout: (id, sessionId) =>
    api.post(`/player/organised-events/${id}/verify`, { sessionId }),
  getNotifications: (unreadOnly) => api.get('/player/notifications', unreadOnly ? { params: { unread_only: 'true' } } : {}),
  getNotificationsUnreadSummary: () => api.get('/player/notifications/unread-summary'),
  getNotificationCount: (messagesOnly) =>
    api.get('/player/notifications/count', messagesOnly ? { params: { messages_only: 'true' } } : {}),
  markNotificationRead: (id) => api.patch(`/player/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post('/player/notifications/mark-all-read'),
  clearAllNotifications: () => api.post('/player/notifications/clear-all'),
  deleteNotification: (id) => api.delete(`/player/notifications/${id}`),
  markNotificationsReadByConversation: (conversationId) =>
    api.post('/player/notifications/mark-read-by-conversation', { conversationId }),
  // Phase 2: open teams & join requests
  getOpenTeams: (params) => api.get('/player/open-teams', { params }),
  getMyJoinRequests: () => api.get('/player/my-join-requests'),
  updateTeamJoinSettings: (teamId, body) => api.patch(`/player/teams/${teamId}/join-settings`, body),
  createSupportTicket: (body) => api.post('/player/support-tickets', body),
  createTeamJoinRequest: (teamId, body) => api.post(`/player/teams/${teamId}/join-requests`, body || {}),
  cancelTeamJoinRequest: (teamId) => api.delete(`/player/teams/${teamId}/join-requests/pending`),
  lookupRegistrationPartner: (emailOrUuid) =>
    api.post('/player/registration/lookup-partner', { emailOrUuid }),
  getRegistrationProducts: (tenantId) =>
    api.get('/player/registration-products', tenantId ? { params: { tenant_id: tenantId } } : {}),
  startRegistrationCheckout: (body) => api.post('/player/registration/start-checkout', body),
  getRegistrationInvoice: (invoiceId) => api.get(`/player/registration-invoices/${invoiceId}`),
  checkoutRegistrationInvoice: (invoiceId, body) =>
    api.post(`/player/registration-invoices/${invoiceId}/checkout`, body || {}),
  verifyRegistrationInvoice: (invoiceId, sessionId) =>
    api.post(`/player/registration-invoices/${invoiceId}/verify`, { sessionId }),
  rejectRegistrationInvite: (invitationId) =>
    api.post(`/player/team-invitations/${invitationId}/reject-registration`),
  claimRegistrationInvites: (inviteToken) =>
    api.post('/player/registration/claim-invites', { inviteToken: inviteToken || null }),
  createRegistrationInvitation: (teamId, body) =>
    api.post(`/player/teams/${teamId}/registration-invitations`, body || {}),
  /** Rolling availability window + optional coarse time_preferences (morning | noon | afternoon | evening). */
  getAvailabilityFortnight: () => api.get('/player/availability/fortnight'),
  saveAvailabilityFortnight: (body) => api.put('/player/availability/fortnight', body),
  getInjuryPeriod: () => api.get('/player/injury-period'),
  setInjuryPeriod: (body) => api.put('/player/injury-period', body),
  clearInjuryPeriod: () => api.delete('/player/injury-period'),
  getHolidayPeriod: () => api.get('/player/holiday-period'),
  setHolidayPeriod: (body) => api.put('/player/holiday-period', body),
  clearHolidayPeriod: () => api.delete('/player/holiday-period'),
  getSeasonForfeitPreview: (teamId) => api.get(`/player/teams/${teamId}/season-forfeit/preview`),
  executeSeasonForfeit: (teamId, body) => api.post(`/player/teams/${teamId}/season-forfeit`, body),
  getSelfLeavePreview: (teamId) => api.get(`/player/teams/${teamId}/leave/preview`),
  executeSelfLeave: (teamId, body) => api.post(`/player/teams/${teamId}/leave`, body),
  /** Admin + system messages for player home (merged on server). */
  getHomeBanners: () => api.get('/player/home-banners'),
  dismissHomeBanner: (bannerId) => api.post(`/player/home-banners/${bannerId}/dismiss`),
  getTeamJoinRequests: (teamId) => api.get(`/player/teams/${teamId}/join-requests`),
  cancelPendingPartnerInvoice: (teamId, invoiceId) =>
    api.delete(`/player/teams/${teamId}/pending-partner-invoices/${invoiceId}`),
  acceptTeamJoinRequest: (teamId, requestId) =>
    api.post(`/player/teams/${teamId}/join-requests/${requestId}/accept`),
  declineTeamJoinRequest: (teamId, requestId) =>
    api.post(`/player/teams/${teamId}/join-requests/${requestId}/decline`),
  ensureJoinRequestConversation: (teamId, requestId) =>
    api.post(`/player/teams/${teamId}/join-requests/${requestId}/conversation`),
  getDiscoveryConversation: (teamId) => api.post(`/player/teams/${teamId}/discovery-conversation`),
  transferTeamCaptain: (teamId, newCaptainPlayerId) =>
    api.post(`/player/teams/${teamId}/transfer-captain`, { newCaptainPlayerId }),
  getOpenPlayers: (params) => api.get('/player/open-players', { params }),
  getPlayerJoinableTeams: (playerId) => api.get(`/player/players/${playerId}/joinable-teams`),
  getPlayerDirectoryConversation: (playerId) =>
    api.post(`/player/players/${playerId}/directory-conversation`),
  searchPlayers: (params) => api.get('/player/players-search', { params }),
  getMyTeamInvitations: () => api.get('/player/team-invitations'),
  getTeamInvitations: (teamId) => api.get(`/player/teams/${teamId}/invitations`),
  createTeamInvitation: (teamId, body) => api.post(`/player/teams/${teamId}/invitations`, body),
  cancelTeamInvitation: (teamId, invitationId) =>
    api.delete(`/player/teams/${teamId}/invitations/${invitationId}`),
  acceptTeamInvitation: (invitationId) => api.post(`/player/team-invitations/${invitationId}/accept`),
  declineTeamInvitation: (invitationId) => api.post(`/player/team-invitations/${invitationId}/decline`),
};

// Player coaching — HPS BFF → CRS (`server/routes/crsProxy.js`). Requires player JWT.
export const coachingAPI = {
  listCoaches: (params = {}) => api.get('/crs/coaches', { params }),
  getCoach: (coachId) => api.get(`/crs/coaches/${coachId}`),
  getOfferings: (coachId) => api.get(`/crs/coaches/${coachId}/offerings`),
  getAvailableSlots: (coachId, params) =>
    api.get(`/crs/coaches/${coachId}/available-slots`, { params }),
  getQuote: (params) => api.get('/crs/coaching/quote', { params }),
  bookFromOffering: (body) => api.post('/crs/bookings/coaching-from-offering', body),
  createCheckoutSession: (orderId) =>
    api.post(`/crs/coaching-orders/${orderId}/checkout-session`, {}),
};

// Dashboard API (supports team_id when logged in as player viewing a team)
export const dashboardAPI = {
  getOverview: (seasonId, teamId, options = {}) => {
    const params = {};
    if (seasonId) params.season_id = seasonId;
    if (teamId) params.team_id = teamId;
    return api.get('/dashboard/overview', { params, ...options });
  },
  getStatistics: (teamId) => {
    const params = teamId ? { team_id: teamId } : {};
    return api.get('/dashboard/statistics', { params });
  },
  getSchedule: (seasonId, teamId) => {
    const params = {};
    if (seasonId) params.season_id = seasonId;
    if (teamId) params.team_id = teamId;
    return api.get('/dashboard/schedule', { params });
  },
  getResults: (seasonId, teamId, options = {}) => {
    const params = {};
    if (seasonId) params.season_id = seasonId;
    if (teamId) params.team_id = teamId;
    return api.get('/dashboard/results', { params, ...options });
  },
  getSeasons: (teamId, campaignId, options = {}) => {
    const params = {};
    if (teamId) params.team_id = teamId;
    if (campaignId) params.campaign_id = campaignId;
    return api.get('/dashboard/seasons', { params, ...options });
  },
};

// Match chat (player + team) — per-team context via team_id; optional X-Tenant-ID in headers for cross-tenant seasons.
export const matchChatAPI = {
  getUnreadCounts: (teamId, tenantId) => {
    const params = teamId ? { team_id: teamId } : {};
    const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
    return api.get('/match-chat/unread-counts', { params, headers });
  },
};

// Divisions (public) API
export const divisionsAPI = {
  // Public leaderboard divisions with teams and mock stats
  getPublicLeaderboard: (params = {}) => api.get('/divisions/leaderboard', { params }),
  // Get recent completed matches for public display (optionally filtered by season_id)
  getRecentMatches: (params = {}) => api.get('/divisions/recent-matches', { params }),
  // Get divisions by canton (for rankings/leaderboard)
  getDivisionsByCanton: (canton, seasonId) => {
    const params = { canton };
    if (seasonId) params.season_id = seasonId;
    return api.get('/divisions/by-canton', { params });
  },
};

// Matches API
export const matchesAPI = {
  // Update match details (date, time, venue)
  updateMatchDetails: (matchId, data, teamId) =>
    api.put(`/matches/${matchId}/details`, {
      ...data,
      ...(teamId ? { team_id: teamId } : {}),
    }),
};

/** Active venue clubs for match details dropdown (tenant-scoped). */
export const clubsAPI = {
  list: (params = {}) => api.get('/clubs', { params }).then((res) => res.data),
};

// Utility functions
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return error.response.data?.message || 'An error occurred';
  } else if (error.request) {
    // Request was made but no response received
    return 'Network error. Please check your connection.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred';
  }
};

export default api;
